import { memo, useRef, useCallback, useState } from 'react';
import type { MouseEvent } from 'react';
import classNames from 'classnames';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconBookmarkFilled } from '@picsart/design-system/foundation/icons/IconBookmarkFilled';
import { IconBookmarkOutline } from '@picsart/design-system/foundation/icons/IconBookmarkOutline';
import { IconLink } from '@picsart/design-system/foundation/icons/IconLink';
import useStyles from './styles';

import CardMedia from '../CardMedia';
import CardOverlays from '../CardOverlays';
import CardActions from '../CardActions';
import CardSkeleton from '../CardSkeleton';
import CardError from '../CardError';
import PresetLikeButton from '../PresetLikeButton';

import useCardVideo from '@/hooks/useCardVideo';
import useCardAudio from '@/hooks/useCardAudio';
import useMediaLoading from '@/hooks/useMediaLoading';
import useTapGesture from '@/hooks/useTapGesture';
import type { CardProps } from './types';

const CardComponent = ({
  imageUrl,
  previews,
  title,
  description,
  isVideo,
  isAudio,
  poster,
  isLoading,
  isSkeleton,
  generationStartTime,
  presetType,
  onClick,
  onDownload,
  onShare,
  onReact,
  reaction,
  onOpenInEditor,
  onSave,
  isSaved,
  onPresetCopyLink,
  isPresetCopyingLink,
  showActions,
  likePreset,
  showHoverOverlay,
  hideVideoIcon,
  showMuteButton,
  muteButtonPlacement,
  fillHeight,
  isSelected,
  error,
  errorType,
  presetTitle,
  onRetry,
  onDismiss,
  owner,
  dataTestId,
  videoLoadDelay,
}: CardProps) => {
  const classes = useStyles();
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const tap = useTapGesture(onClick);

  const {
    mediaLoaded,
    shouldLoadVideo,
    videoReady,
    handleImageLoad,
    setVideoReadyTrue,
  } = useMediaLoading({ cardRef, imageUrl, isVideo, poster, videoLoadDelay });

  const { handleLoadedMetadata, handleCanPlay } = useCardVideo({
    videoRef,
    isVideo,
    shouldLoadVideo,
  });

  const { isMuted, hasAudio, handleVideoLoad, handleMuteToggle } = useCardAudio(
    {
      videoRef,
      isVideo,
      showMuteButton,
      shouldLoadVideo,
      videoSourceId: imageUrl,
    }
  );

  const onCanPlay = useCallback(
    () => handleCanPlay(setVideoReadyTrue),
    [handleCanPlay, setVideoReadyTrue]
  );

  const handleSave = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onSave?.();
    },
    [onSave]
  );

  const handlePresetCopyLink = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onPresetCopyLink?.();
    },
    [onPresetCopyLink]
  );

  if (isSkeleton || isLoading) {
    return (
      <CardSkeleton
        isLoading={isLoading}
        generationStartTime={generationStartTime}
        presetType={presetType}
      />
    );
  }

  if (error && onRetry && onDismiss) {
    return (
      <CardError
        errorMessage={error}
        errorType={errorType}
        presetTitle={presetTitle}
        onRetry={onRetry}
        onDismiss={onDismiss}
      />
    );
  }

  const rootClassName = classNames(classes.root, {
    [classes.rootFill]: fillHeight,
  });
  const mediaContainerClassName = classNames(classes.mediaContainer, {
    [classes.mediaContainerFill]: fillHeight,
    [classes.mediaContainerSelected]: isSelected,
  });

  return (
    <div
      ref={cardRef}
      className={rootClassName}
      data-testid={dataTestId}
      onClick={onClick ? tap.onClick : undefined}
      onPointerDown={onClick ? tap.onPointerDown : undefined}
      onPointerMove={onClick ? tap.onPointerMove : undefined}
      onPointerCancel={onClick ? tap.onPointerCancel : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={(e) => {
        // Only when the card itself holds focus. The overlay controls are real
        // buttons, so their Enter/Space bubbles up here too — without this the
        // key that copies a link would also open the preset.
        if (e.target !== e.currentTarget) return;
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={mediaContainerClassName}>
        <CardMedia
          videoRef={videoRef}
          imageUrl={imageUrl}
          previews={previews}
          title={title}
          isVideo={isVideo}
          isAudio={isAudio}
          poster={poster}
          hideVideoIcon={hideVideoIcon}
          showMuteButton={showMuteButton}
          muteButtonPlacement={muteButtonPlacement}
          mediaLoaded={mediaLoaded}
          shouldLoadVideo={shouldLoadVideo}
          videoReady={videoReady}
          isMuted={isMuted}
          hasAudio={hasAudio}
          onImageLoad={handleImageLoad}
          onVideoLoad={handleVideoLoad}
          onLoadedMetadata={handleLoadedMetadata}
          onCanPlay={onCanPlay}
          onMuteToggle={handleMuteToggle}
        />
        <CardOverlays
          title={title}
          description={description}
          hasLiveVideo={shouldLoadVideo}
          owner={owner}
          showHoverOverlay={showHoverOverlay}
          isParentHovered={isHovered}
        />
        {onSave && (
          <InlineButton
            size="small"
            centerIcon={isSaved ? IconBookmarkFilled : IconBookmarkOutline}
            className={classes.saveButton}
            isWithoutBorder
            isWithoutBackground
            onClick={handleSave}
            aria-label={isSaved ? 'Remove from saved' : 'Save'}
            dataTestId="card-save"
          />
        )}
        {(onPresetCopyLink || likePreset) && (
          <div
            className={classNames(classes.cornerActions, {
              [classes.cornerActionsVisible]: isHovered || isPresetCopyingLink,
            })}
          >
            {onPresetCopyLink && (
              <InlineButton
                size="small"
                centerIcon={isPresetCopyingLink ? undefined : IconLink}
                onClick={handlePresetCopyLink}
                isDisabled={isPresetCopyingLink}
                isLoading={isPresetCopyingLink}
                aria-label={isPresetCopyingLink ? 'Copying link' : 'Copy link'}
                className={classes.cornerButton}
                dataTestId="card-preset-copy-link"
                isWithoutBorder
              />
            )}
            {likePreset && (
              <PresetLikeButton
                {...likePreset}
                size="card"
                className={classes.cornerButton}
              />
            )}
          </div>
        )}
        {showActions && (
          <CardActions
            onDownload={onDownload}
            onShare={onShare}
            onReact={onReact}
            reaction={reaction}
            onOpenInEditor={onOpenInEditor}
            isParentHovered={isHovered}
          />
        )}
      </div>
    </div>
  );
};

const Card = memo(CardComponent);
export default Card;
